import * as Sentry from '@sentry/react';
import Dexie, { Table } from 'dexie';

// Define interfaces for database entities
interface UserInfo {
  id?: number;
  user_id: string;
  experience_id: string;
  currentView: string;
  student_updated_at: number;
  teacher_updated_at: number;
  active_students?: any;
  focus_lost_count?: number;
  student_unread_count?: number;
  online_priority_keys?: string[];
  [key: string]: any;
}

interface UserResponse {
  id?: number;
  user_id: string;
  experience_id: string;
  segment_id: string;
  question_id: string;
  user_response_id: string;
  updated_at: number;
  [key: string]: any;
}

interface Log {
  id?: number;
  log_id: string;
  user_id: string;
  experience_id: string;
  by_user_id: string;
  internet_status: string;
  [key: string]: any;
}

// Define options interfaces
interface OpenOfflineDBOptions {
  updateOfflineDBStatus?: (status: string) => void;
}

interface SaveItemOptions {
  updateOfflineSaveStatus?: (status: string) => void;
}

interface GetItemsOptions {
  filterBy?: Record<string, any>;
}

interface DeleteItemsOptions {
  [key: string]: any;
}

// Define database class
class OfflineDatabase extends Dexie {
  userInfo!: Table<UserInfo>;
  userResponses!: Table<UserResponse>;
  logs!: Table<Log>;

  constructor() {
    super('Assessprep');
    this.version(5).stores({
      userInfo: '++id,user_id,experience_id,currentView,student_updated_at,teacher_updated_at',
      userResponses:
        '++id,user_id,experience_id,segment_id,question_id,&user_response_id,updated_at',
      logs: '++id,log_id,user_id,experience_id,by_user_id,internet_status',
    });
  }
}

export const db = new OfflineDatabase();
// const isMobile = checkMob();

/////////////////////
// NOTE: Increase version number when ever update index keys
/////////////////////

let isIndexedDBSupported = typeof window !== 'undefined' && window.indexedDB ? true : false;

// Server-authoritative keys: the server value always wins the hydration
// merge, and the hydration write-back never sends them client->server.
export const onlinePriorityKeys = [
  'extra_time',
  'extra_time_offset',
  'security',
  'request_resume',
  'needs_attention_at',
  'student_unread_count',
  // Pause-related fields are teacher-owned on the wire; the offline
  // student-cache must NEVER clobber a teacher's pause write. Restored
  // (and `pause_status` added) after the regression in df84c775e.
  // See APL-11061 China comment thread 62f85716.
  'pause_status',
  'paused',
  'paused_at',
  'unpaused_at',
  'unpaused_offset',
  // Extra-time grants are teacher-owned wire fields; after a local apply the
  // Dexie row permanently holds pending_extra_time:null, so a newer offline
  // student row must never clobber a fresh grant. R3-TJ5/R3-L2, 2026-07-24.
  'pending_extra_time',
  'accommodations',
  // Teacher-owned proctoring flag — a stale Dexie row flipping it on a
  // chat-triggered merge fired spurious "Refresh page" toasts (APL-11850 §12).
  'talview',
  'teacher_updated_at',
  // Teacher-owned only — a student-owned key here would let the server's older
  // copy wipe work done offline.
  'duration_after_reset',
  // OWNERSHIP/claim keys (J25 China resume loop): a stale Dexie session_id
  // re-asserted by the write-back kicked the other browser to Resume in a
  // two-browser ping-pong. These keys move only via explicit online actions
  // (Resume/Start/End, boot claim) — never via hydration merge or write-back.
  'session_id',
  'request_resume_session_id',
  'deviceInfo',
  'uploadDeviceInfo',
  'usedDevices',
  // APL-12236 (CN7-45/47/48/54/61/62/63/64): teacher-owned browser grant
  // (MonitorRowActions' Allow-in-browser switch). A stale Dexie row reverted a
  // fresh grant, and with the grant gone ExperienceTakeRoot's
  // `appType === 'web' && !allowedInBrowser` gate replaces the ENTIRE exam
  // surface with TakeLockdownOnlyView ("Start in App") mid-paper — the China QA
  // round's "start view keeps showing randomly" / "start in app screen" /
  // flicker reports. Riding this list fixes it twice over: the shield forces the
  // server value for rows ALREADY on disk, and the write-strip keeps the key out
  // of Dexie from here on. Safe to strip: the Dexie userInfo row is only ever a
  // merge input alongside a live server row (useResolvedRealtimeUserInfo returns
  // early without one), never a standalone offline source.
  'allowed_in_browser',
  // APL-12236: teacher-side CHAT PRESENCE. Written only by TeacherDashboardChat
  // (:129-143, :158-159); the student owns the mirror set instead
  // (is_student_active / chat_student_last_seen_at / active_students), so these
  // three are teacher-owned. Unshielded, a stale Dexie row re-asserted them over
  // the live values and the boot write pushed that back to the server. Same
  // shape as the talview flip that APL-11850 §12 fixed by shielding.
  'is_teacher_active',
  'active_teachers',
  'chat_teacher_last_seen_at',
  // Server-owned attempt number. The LOCAL copy is stamped only by
  // stampOfflineAttemptNo (useStaleAttemptWipe) — strip + shield everywhere else.
  'attempt_no',
  // APL-12236: superadmin-only switch, written solely by the BE
  // (experience_users_controller#update_enable_local_storage_sync); Rails
  // custom_fields is the source of truth and the student never writes it.
  'enable_local_storage_sync',
  //
  // DELIBERATELY NOT SHIELDED, checked and rejected in the same pass (APL-12236)
  // — see the negative pins in useResolvedRealtimeUserInfo.test.ts:
  //   has_student_read      — MIXED: the student writes `true` when they read the
  //                           message (StudentChatTool.tsx:89). Shielding it lets
  //                           a stale server `false` resurrect a phantom unread
  //                           badge, and mixed-ownership fields need event-scoped
  //                           invalidation, not a permanent shield.
  //   has_teacher_read      — MIXED too, and it was briefly shielded here in
  //                           error. The teacher writes `true`
  //                           (TeacherDashboardChat.tsx:131) but the STUDENT
  //                           writes `false` after sending a message
  //                           (StudentChatTool.tsx:205). Shielding it lets a
  //                           late emission flip the store back to a stale
  //                           `true`, after which StudentChatTool's
  //                           teacherStateRef skips the write and the teacher's
  //                           unread chip (MonitorRowActions.tsx:74) never
  //                           lights for that message. Same rule as above: the
  //                           student half of a mirrored pair is never shielded.
  //   attachments_count /   — STUDENT-written (useRealtimeUserResponses.ts:333-336,
  //   total_attachments_json  studentUploadApi.ts:33). Shielding would discard a
  //                           student's offline uploads. Never add these.
];

// iOS IndexedDB can wedge and never call back (neither resolve nor reject) —
// bound db.open() so boot proceeds as 'closed' instead of hanging the exam.
export const OFFLINE_DB_OPEN_TIMEOUT_MS = 10_000;

export function openOfflineDB(options: OpenOfflineDBOptions): void {
  const {
    updateOfflineDBStatus = () => {
      /* no-op */
    },
  } = options;
  if (isIndexedDBSupported) {
    // First settle wins: a post-timeout resolve/reject must not re-report.
    let settled = false;
    const settle = (status: string) => {
      if (settled) return;
      settled = true;
      if (updateOfflineDBStatus) {
        updateOfflineDBStatus(status);
      }
    };
    const timeoutId = setTimeout(() => {
      Sentry.addBreadcrumb({
        category: 'offline-db',
        message: `openOfflineDB: db.open() gave no callback within ${OFFLINE_DB_OPEN_TIMEOUT_MS}ms — proceeding as closed`,
        level: 'warning',
      });
      isIndexedDBSupported = false;
      settle('closed');
    }, OFFLINE_DB_OPEN_TIMEOUT_MS);
    db.open()
      .then(() => {
        clearTimeout(timeoutId);
        // A post-timeout resolve must not start the retention sweep mid-exam
        // — the DB is already reported 'closed' and writers are no-oping.
        if (!settled) {
          clearOldItemsFromDB(180);
        }
        settle('open');
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.log('Error in opening indexed DB, Disabling saving item offline ==>', error);
        settle('closed');
        isIndexedDBSupported = false;
        Sentry.captureException(error);
      });
  } else {
    console.log('Indexed DB is not supported in this browser');
    if (updateOfflineDBStatus) {
      updateOfflineDBStatus('not_supported');
    }
    Sentry.captureException(isIndexedDBSupported);
  }
}

export async function saveItemToOfflineDB(
  tableName: keyof OfflineDatabase,
  searchObj: Record<string, any>,
  updateObject: Record<string, any>,
  options: SaveItemOptions = {},
  retry = true
): Promise<void> {
  const {
    updateOfflineSaveStatus = () => {
      /* no-op */
    },
  } = options;

  console.log(
    'saveItemToOfflineDB tableName, searchObj, updateObject ==>',
    tableName,
    searchObj,
    updateObject
  );

  // if (isMobile) {
  //   // in mobile firestore by default uses indexed db for synchronisation
  //   return;
  // }

  if (!isIndexedDBSupported) {
    console.log('Indexed DB is not supported in this browser');
    return;
  }

  try {
    let dbItem = null;
    if (Object.keys(searchObj).length > 0) {
      dbItem = await (db as any)[tableName].where(searchObj).first();
    }

    const itemToUpdate = {
      ...searchObj,
      ...updateObject,
    };

    if (tableName === 'userInfo') {
      delete itemToUpdate.active_students; // Offline db does not support firestore query object
      delete itemToUpdate.focus_lost_count;
      delete itemToUpdate.student_unread_count;
      // delete itemToUpdate.all_active_session_ids;
      // TODO - right now if you go offline, focus lost logs will be tracked and synced but count will not update
      // Not saving teacher action keys offline as they always have online
      // priority — keyed on the exported constant: the wire field
      // online_priority_keys is stripped from writes (LEGACY_ABSENT_COLUMNS)
      // and can never arrive on the row (J25-N08 leftover).
      onlinePriorityKeys.forEach((key) => {
        delete itemToUpdate[key];
      });
    }

    console.log(
      'saveItemToOfflineDB tableName, itemToUpdate, dbItem',
      tableName,
      itemToUpdate,
      dbItem
    );

    if (!dbItem) {
      // Change from add to put => put does both adds or updates data as per unique indexes
      (db as any)[tableName]
        .put(itemToUpdate)
        .then(() => {
          if (updateOfflineSaveStatus) {
            updateOfflineSaveStatus('saved');
          }
          console.log('successfully updated ==>', itemToUpdate);
        })
        .catch(function (e: any) {
          console.error('Error in put saveItemToOfflineDB ==>', e, itemToUpdate, dbItem);
          if (updateOfflineSaveStatus) {
            updateOfflineSaveStatus('failed');
          }
          Sentry.captureException(e);
          throw e; // Re-throw the error to abort flow!
        });
    } else {
      (db as any)[tableName]
        .update(dbItem.id, itemToUpdate)
        .then(() => {
          if (updateOfflineSaveStatus) {
            updateOfflineSaveStatus('saved');
          }
          console.log('successfully updated ==>', itemToUpdate);
        })
        .catch(function (e: any) {
          console.error(
            'Error in updating saveItemToOfflineDB ==>',
            e,
            itemToUpdate,
            dbItem,
            e.name,
            e.inner
          );
          if (updateOfflineSaveStatus) {
            updateOfflineSaveStatus('failed');
          }
          Sentry.captureException(e);
          throw e; // Re-throw the error to abort flow!
        });
    }
  } catch (error) {
    console.error('Error in saveItemToOfflineDB ==>', error, tableName, searchObj, updateObject);
    console.log('Is DB Open and retry allowed', db.isOpen(), retry);
    // if (error.name === "InvalidStateError" && db.isOpen() && retry) {
    if (db.isOpen() && retry) {
      // Re-Trying closing and opening db for all errors
      console.log('Dexie: Need to reopen db');
      db.close();
      db.open()
        .then(() => {
          // Sentry.captureException(error);
          saveItemToOfflineDB(tableName, searchObj, updateObject, options, false);
        })
        .catch((reopenError) => {
          Sentry.captureException(reopenError, { extra: { op: 'saveItemToOfflineDB:reopen' } });
        });
    } else {
      console.log('Something is wrong not resolved after retry', retry, error);
      if (updateOfflineSaveStatus) {
        updateOfflineSaveStatus('failed');
      }
      Sentry.captureException(error);
    }
  }
}

export async function getItemsFromOfflineDB(
  tableName: keyof OfflineDatabase,
  searchObj: Record<string, any>,
  options: GetItemsOptions = {},
  retry = true
): Promise<any[]> {
  const { filterBy } = options;
  console.log('getItemsFromOfflineDB tableName, searchObj', tableName, searchObj);

  // if (isMobile) {
  //   // in mobile firestore by default uses indexed db for synchronisation
  //   return [];
  // }

  if (!isIndexedDBSupported) {
    console.log('Indexed DB is not supported in this browser');
    return [];
  }

  try {
    // Get all items from the table that match the search criteria
    const dbItems = (await (db as any)[tableName].where(searchObj).toArray()) || [];

    // Apply additional filtering if filterBy options are provided
    let finalDBItems = dbItems;
    if (filterBy) {
      finalDBItems = dbItems.filter((item: any) => {
        // Check that item matches all filter conditions
        return Object.entries(filterBy).every(([key, allowedValues]) => {
          if (Array.isArray(allowedValues)) {
            // If filter value is array, check item value is included
            return allowedValues.includes(item[key]);
          }
          // Otherwise do exact match
          return item[key] === allowedValues;
        });
      });
    }

    console.log('getItemsFromOfflineDB dbItemArr, dbItem', tableName, searchObj, finalDBItems);

    return finalDBItems;
  } catch (error) {
    console.error('Error in getItemsFromOfflineDB ==>', error, tableName, searchObj);
    console.log('Is DB Open and retry allowed', db.isOpen(), retry);
    // if (error.name === "InvalidStateError" && db.isOpen() && retry) {
    if (db.isOpen() && retry) {
      // Re-Trying closing and opening db for all errors
      console.log('Dexie: Need to reopen db');
      db.close();
      return await db
        .open()
        .then(() => {
          // Sentry.captureException(error);
          return getItemsFromOfflineDB(tableName, searchObj, options, false);
        })
        .catch((reopenError) => {
          Sentry.captureException(reopenError, { extra: { op: 'getItemsFromOfflineDB:reopen' } });
          throw reopenError; // preserve existing propagation to caller
        });
    } else {
      console.log('Something is wrong not resolved after retry', retry, error);
      Sentry.captureException(error);
      return [];
    }
  }
}

export async function deleteItemsFromOfflineDB(
  tableName: keyof OfflineDatabase,
  searchObj: Record<string, any>,
  options: DeleteItemsOptions = {},
  retry = true
): Promise<void> {
  console.log('deleteItemsFromOfflineDB tableName, searchObj', tableName, searchObj);

  // if (isMobile) {
  //   // in mobile firestore by default uses indexed db for synchronisation
  //   return;
  // }

  if (!isIndexedDBSupported) {
    console.log('Indexed DB is not supported in this browser');
    return;
  }

  try {
    // Before deleting data make sure offline data saved on firestore
    const dbItems = await (db as any)[tableName].where(searchObj).delete();

    console.log('deleteItemsFromOfflineDB dbItemArr, dbItem', dbItems);
  } catch (error) {
    console.error('Error in deleteItemsFromOfflineDB ==>', error, tableName, searchObj);

    console.log('Is DB Open and retry allowed', db.isOpen(), retry);
    // if (error.name === "InvalidStateError" && db.isOpen() && retry) {
    if (db.isOpen() && retry) {
      // Re-Trying closing and opening db for all errors
      console.log('Dexie: Need to reopen db');
      db.close();
      await db
        .open()
        .then(() => {
          // Sentry.captureException(error);
          deleteItemsFromOfflineDB(tableName, searchObj, options, false);
        })
        .catch((reopenError) => {
          Sentry.captureException(reopenError, {
            extra: { op: 'deleteItemsFromOfflineDB:reopen' },
          });
        });
    } else {
      console.log('Something is wrong not resolved after retry', retry, error);
      Sentry.captureException(error);
    }
  }
}

// Attempt number: read/stamped ONLY by useStaleAttemptWipe —
// attempt_no rides onlinePriorityKeys, so every other userInfo write strips it.
export async function getOfflineAttemptNo(
  experienceId: string | number,
  userId: string | number,
  retry = true
): Promise<number | null> {
  if (!isIndexedDBSupported) return null;
  try {
    // Rows written before the id was normalized hold user_id as text; a missed
    // text row would read as "no attempt" and wipe a current attempt.
    const row =
      (await db.userInfo.where({ experience_id: experienceId, user_id: Number(userId) }).first()) ??
      (await db.userInfo.where({ experience_id: experienceId, user_id: String(userId) }).first());
    return row?.attempt_no ?? null;
  } catch (error) {
    if (db.isOpen() && retry) {
      db.close();
      return await db
        .open()
        .then(() => getOfflineAttemptNo(experienceId, userId, false))
        .catch((reopenError) => {
          Sentry.captureException(reopenError, { extra: { op: 'getOfflineAttemptNo:reopen' } });
          throw reopenError;
        });
    }
    Sentry.captureException(error, { extra: { op: 'getOfflineAttemptNo' } });
    // A read failure is NOT "no attempt stamped" — the caller must skip the
    // wipe rather than treat this experience as attempt 1.
    throw error;
  }
}

export async function stampOfflineAttemptNo(
  experienceId: string | number,
  userId: number,
  attemptNo: number
): Promise<void> {
  if (!isIndexedDBSupported) return;
  try {
    const row = await db.userInfo.where({ experience_id: experienceId, user_id: userId }).first();
    if (row?.id != null) {
      await db.userInfo.update(row.id, { attempt_no: attemptNo });
    } else {
      await db.userInfo.put({
        experience_id: experienceId,
        user_id: userId,
        attempt_no: attemptNo,
      } as unknown as UserInfo);
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { op: 'stampOfflineAttemptNo' } });
  }
}

export async function clearAlreadySubmittedExperiencesFromDB(
  options: DeleteItemsOptions = {},
  retry = true
): Promise<void> {
  console.log('clearAlreadySubmittedExperiencesFromDB');

  // if (isMobile) {
  //   // in mobile firestore by default uses indexed db for synchronisation
  //   return;
  // }

  if (!isIndexedDBSupported) {
    console.log('Indexed DB is not supported in this browser');
    return;
  }

  try {
    const dbItems = (await db.userInfo.where({ currentView: 'submittedTest' }).toArray()) || [];

    dbItems.forEach((item: UserInfo) => {
      deleteItemsFromOfflineDB('userInfo', {
        experience_id: item.experience_id,
        user_id: item.user_id,
      });
      deleteItemsFromOfflineDB('userResponses', {
        experience_id: item.experience_id,
        user_id: item.user_id,
      });
      deleteItemsFromOfflineDB('logs', {
        experience_id: item.experience_id,
        user_id: item.user_id,
      });
    });
    console.log('clearAlreadySubmittedExperiencesFromDB dbItemArr, dbItem', dbItems);
  } catch (error) {
    console.error('Error in clearAlreadySubmittedExperienceFromDB ==>', error);
    console.log('Is DB Open and retry allowed', db.isOpen(), retry);
    // if (error.name === "InvalidStateError" && db.isOpen() && retry) {
    if (db.isOpen() && retry) {
      // Re-Trying closing and opening db for all errors
      console.log('Dexie: Need to reopen db');
      db.close();
      await db
        .open()
        .then(() => {
          // Sentry.captureException(error);
          clearAlreadySubmittedExperiencesFromDB(options, false);
        })
        .catch((reopenError) => {
          Sentry.captureException(reopenError, {
            extra: { op: 'clearAlreadySubmittedExperiencesFromDB:reopen' },
          });
        });
    } else {
      console.log('Something is wrong not resolved after retry', retry, error);
      Sentry.captureException(error);
    }
  }
}

export async function clearOldItemsFromDB(
  days = 90,
  options: DeleteItemsOptions = {},
  retry = true
): Promise<void> {
  console.log('clearOldItemsFromDB');

  // if (isMobile) {
  //   // in mobile firestore by default uses indexed db for synchronisation
  //   return;
  // }

  if (!isIndexedDBSupported) {
    console.log('Indexed DB is not supported in this browser');
    return;
  }

  try {
    // Will delete all experiences data whose user info student_updated_at, teacher_updated_at is before 30 days
    const beforeTime = days * 24 * 60 * 60 * 1000;
    const dbItems =
      (await db.userInfo
        .where('currentView')
        .equals('submittedTest')
        .or('student_updated_at')
        .belowOrEqual(Date.now() - beforeTime)
        .or('teacher_updated_at')
        .belowOrEqual(Date.now() - beforeTime)
        .toArray()) || [];

    dbItems.forEach((item: UserInfo) => {
      deleteItemsFromOfflineDB('userInfo', {
        experience_id: item.experience_id,
        user_id: item.user_id,
      });
      deleteItemsFromOfflineDB('userResponses', {
        experience_id: item.experience_id,
        user_id: item.user_id,
      });
      deleteItemsFromOfflineDB('logs', {
        experience_id: item.experience_id,
        user_id: item.user_id,
      });
    });
    console.log('clearOldItemsFromDB dbItemArr, dbItem', dbItems);
  } catch (error) {
    console.error('Error in clearOldItemsFromDB ==>', error);
    console.log('Is DB Open and retry allowed', db.isOpen(), retry);
    // if (error.name === "InvalidStateError" && db.isOpen() && retry) {
    if (db.isOpen() && retry) {
      // Re-Trying closing and opening db for all errors
      console.log('Dexie: Need to reopen db');
      db.close();
      await db
        .open()
        .then(() => {
          // Sentry.captureException(error);
          clearOldItemsFromDB(days, options, false);
        })
        .catch((reopenError) => {
          Sentry.captureException(reopenError, { extra: { op: 'clearOldItemsFromDB:reopen' } });
        });
    } else {
      console.log('Something is wrong not resolved after retry', retry, error);
      Sentry.captureException(error);
    }
  }
}

// TODO: clear old indexed db data on the basis of submit or currentView or Old Date
// clearAlreadySubmittedExperiencesFromDB()
